import React, { useState } from 'react';
import { Input, Button, Space, Card, Table, Empty, Radio, Checkbox } from 'antd';
import { SearchOutlined, ClearOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import PermissionGuard from '../../components/PermissionGuard';
import { usePWG302Query } from './hooks/usePWG302Query';
import type { ARRow, PaymentStatusFilter } from '../../services/pwg302Api';
import './PWG302Form.css';

const MODULE_CODE = 'pwg302f1';

const fmtDate = (v: string) => v?.substring(0, 10) || '';
const fmtNum = (v: number) =>
  v == null ? '' : v.toLocaleString('zh-TW', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const FilterLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label className="pwg302-filter-label">{children}</label>
);

const columns: ColumnsType<ARRow> = [
  { title: '客戶代號', dataIndex: 'cust_code', key: 'cust_code', width: 90, fixed: 'left' as const },
  { title: '客戶名稱', dataIndex: 'cust_name', key: 'cust_name', width: 150, ellipsis: true },
  { title: '交運單號', dataIndex: 'inv_no', key: 'inv_no', width: 120 },
  { title: '交運日期', dataIndex: 'delivery_date', key: 'delivery_date', width: 100, render: fmtDate },
  { title: '幣別', dataIndex: 'currency', key: 'currency', width: 60, align: 'center' as const },
  { title: '單據', dataIndex: 'doc_type', key: 'doc_type', width: 55, align: 'center' as const },
  { title: '匯率', dataIndex: 'exchange_rate', key: 'exchange_rate', width: 70, align: 'right' as const, render: (v: number) => v?.toFixed(4) || '' },
  { title: '應收金額', dataIndex: 'inv_amt', key: 'inv_amt', width: 110, align: 'right' as const, render: fmtNum },
  { title: '已收金額', dataIndex: 'paid_amt', key: 'paid_amt', width: 110, align: 'right' as const, render: fmtNum },
  { title: '餘額', dataIndex: 'balance', key: 'balance', width: 110, align: 'right' as const, render: fmtNum },
  { title: '業務員', dataIndex: 'sale_no', key: 'sale_no', width: 80 },
  { title: '業務員名稱', dataIndex: 'sale_name', key: 'sale_name', width: 100, ellipsis: true },
  { title: '部門代號', dataIndex: 'dept_no', key: 'dept_no', width: 80 },
  { title: '部門名稱', dataIndex: 'dept_name', key: 'dept_name', width: 110, ellipsis: true },
];

const PWG302Form: React.FC = () => {
  const { loading, data, queried, noData, runQuery, reset } = usePWG302Query();

  const [custFrom, setCustFrom] = useState('');
  const [custTo, setCustTo] = useState('');
  const [invFrom, setInvFrom] = useState('');
  const [invTo, setInvTo] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expectedDateFrom, setExpectedDateFrom] = useState('');
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatusFilter>(3);
  const [includeZeroBalance, setIncludeZeroBalance] = useState(false);
  const [saleFrom, setSaleFrom] = useState('');
  const [saleTo, setSaleTo] = useState('');
  const [deptFrom, setDeptFrom] = useState('');
  const [deptTo, setDeptTo] = useState('');

  const handleQuery = () => {
    runQuery({
      custFrom: custFrom.trim() || undefined,
      custTo: custTo.trim() || undefined,
      invFrom: invFrom.trim() || undefined,
      invTo: invTo.trim() || undefined,
      dateFrom: dateFrom.trim() || undefined,
      dateTo: dateTo.trim() || undefined,
      expectedDateFrom: expectedDateFrom.trim() || undefined,
      paymentStatus,
      includeZeroBalance,
      saleFrom: saleFrom.trim() || undefined,
      saleTo: saleTo.trim() || undefined,
      deptFrom: deptFrom.trim() || undefined,
      deptTo: deptTo.trim() || undefined,
    });
  };

  const handleReset = () => {
    setCustFrom(''); setCustTo('');
    setInvFrom(''); setInvTo('');
    setDateFrom(''); setDateTo('');
    setExpectedDateFrom('');
    setPaymentStatus(3);
    setIncludeZeroBalance(false);
    setSaleFrom(''); setSaleTo('');
    setDeptFrom(''); setDeptTo('');
    reset();
  };

  const handlePrint = () => {
    if (!data || data.length === 0) return;
    const win = window.open('', '_blank');
    if (!win) return;
    const totalInv = data.reduce((s, r) => s + (r.inv_amt || 0), 0);
    const totalPaid = data.reduce((s, r) => s + (r.paid_amt || 0), 0);
    const totalBal = data.reduce((s, r) => s + (r.balance || 0), 0);
    win.document.write(`<!DOCTYPE html><html><head><title>應收帳款明細/統計</title>
<style>body{font-family:'Microsoft JhengHei',sans-serif;margin:20px;font-size:11px}h2{text-align:center}table{width:100%;border-collapse:collapse}th,td{border:1px solid #333;padding:2px 5px}th{background:#f0f0f0;text-align:center}.right{text-align:right}.total{font-weight:bold;background:#fffbe6}@media print{body{margin:10mm}}</style>
</head><body>
<h2>應收帳款明細/統計</h2>
<table><thead><tr><th>客戶代號</th><th>客戶名稱</th><th>交運單號</th><th>交運日期</th><th>幣別</th><th>單據</th><th>匯率</th><th>應收金額</th><th>已收金額</th><th>餘額</th><th>業務員</th><th>部門</th></tr></thead><tbody>
${data.map(r => `<tr>
  <td>${r.cust_code}</td><td>${r.cust_name}</td><td>${r.inv_no}</td>
  <td>${fmtDate(r.delivery_date)}</td><td style="text-align:center">${r.currency}</td>
  <td style="text-align:center">${r.doc_type}</td><td class="right">${r.exchange_rate?.toFixed(4) || ''}</td>
  <td class="right">${fmtNum(r.inv_amt)}</td><td class="right">${fmtNum(r.paid_amt)}</td>
  <td class="right">${fmtNum(r.balance)}</td><td>${r.sale_name}</td><td>${r.dept_name}</td>
</tr>`).join('')}
<tr class="total"><td colspan="7" style="text-align:right">合計</td>
  <td class="right">${fmtNum(totalInv)}</td><td class="right">${fmtNum(totalPaid)}</td>
  <td class="right">${fmtNum(totalBal)}</td><td colspan="2"></td>
</tr>
</tbody></table></body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  const totalInv = data.reduce((s, r) => s + (r.inv_amt || 0), 0);
  const totalPaid = data.reduce((s, r) => s + (r.paid_amt || 0), 0);
  const totalBal = data.reduce((s, r) => s + (r.balance || 0), 0);

  return (
    <PermissionGuard module={MODULE_CODE} action="query">
      <div className="pwg302-form">
        <div className="pwg302-header">
          <h2>應收帳款明細/統計</h2>
          {queried && !noData && (
            <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印</Button>
          )}
        </div>

        <Card size="small" style={{ marginBottom: 16 }}>
          <Space wrap size="middle">
            <div>
              <FilterLabel>客戶代號起</FilterLabel>
              <Input value={custFrom} onChange={e => setCustFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear />
            </div>
            <div>
              <FilterLabel>客戶代號迄</FilterLabel>
              <Input value={custTo} onChange={e => setCustTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear />
            </div>
            <div>
              <FilterLabel>交運單號起</FilterLabel>
              <Input value={invFrom} onChange={e => setInvFrom(e.target.value)} placeholder="起" style={{ width: 130 }} allowClear />
            </div>
            <div>
              <FilterLabel>交運單號迄</FilterLabel>
              <Input value={invTo} onChange={e => setInvTo(e.target.value)} placeholder="迄" style={{ width: 130 }} allowClear />
            </div>
            <div>
              <FilterLabel>交運日期起</FilterLabel>
              <Input value={dateFrom} onChange={e => setDateFrom(e.target.value)} placeholder="YYYY-MM-DD" style={{ width: 130 }} allowClear />
            </div>
            <div>
              <FilterLabel>交運日期迄</FilterLabel>
              <Input value={dateTo} onChange={e => setDateTo(e.target.value)} placeholder="YYYY-MM-DD" style={{ width: 130 }} allowClear />
            </div>
            <div>
              <FilterLabel>預計收款日起</FilterLabel>
              <Input value={expectedDateFrom} onChange={e => setExpectedDateFrom(e.target.value)} placeholder="YYYY-MM-DD" style={{ width: 130 }} allowClear />
            </div>
          </Space>
          <Space wrap size="middle" style={{ marginTop: 10 }}>
            <div>
              <FilterLabel>業務員起</FilterLabel>
              <Input value={saleFrom} onChange={e => setSaleFrom(e.target.value)} placeholder="起" style={{ width: 100 }} allowClear />
            </div>
            <div>
              <FilterLabel>業務員迄</FilterLabel>
              <Input value={saleTo} onChange={e => setSaleTo(e.target.value)} placeholder="迄" style={{ width: 100 }} allowClear />
            </div>
            <div>
              <FilterLabel>部門起</FilterLabel>
              <Input value={deptFrom} onChange={e => setDeptFrom(e.target.value)} placeholder="起" style={{ width: 100 }} allowClear />
            </div>
            <div>
              <FilterLabel>部門迄</FilterLabel>
              <Input value={deptTo} onChange={e => setDeptTo(e.target.value)} placeholder="迄" style={{ width: 100 }} allowClear />
            </div>
            <div>
              <FilterLabel>預計收款</FilterLabel>
              <Radio.Group
                value={paymentStatus}
                onChange={e => setPaymentStatus(e.target.value as PaymentStatusFilter)}
              >
                <Radio value={1}>有預計收款</Radio>
                <Radio value={2}>無預計收款</Radio>
                <Radio value={3}>全部</Radio>
              </Radio.Group>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', paddingTop: 18 }}>
              <Checkbox
                checked={includeZeroBalance}
                onChange={e => setIncludeZeroBalance(e.target.checked)}
              >
                包含零餘額
              </Checkbox>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery} loading={loading}>
                  查詢
                </Button>
                <Button icon={<ClearOutlined />} onClick={handleReset}>清除</Button>
              </Space>
            </div>
          </Space>
        </Card>

        {queried && noData && <Empty description="查無此資料" style={{ marginTop: 40 }} />}

        {queried && !noData && (
          <>
            <div className="pwg302-summary">
              <span>共 <strong>{data.length}</strong> 筆</span>
              <span>應收合計：<strong>{fmtNum(totalInv)}</strong></span>
              <span>已收合計：<strong>{fmtNum(totalPaid)}</strong></span>
              <span>餘額合計：<strong>{fmtNum(totalBal)}</strong></span>
            </div>
            <Table<ARRow>
              columns={columns}
              dataSource={data}
              rowKey={(r) => `${r.cust_code}-${r.inv_no}`}
              loading={loading}
              size="small"
              pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }}
              scroll={{ x: 1200 }}
            />
          </>
        )}
      </div>
    </PermissionGuard>
  );
};

export default PWG302Form;
